import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';

import IVersion from '../IVersion';

/**
 * Which alternative (version) is being edited, and making it the primary one or removing it.
 */
const VersionActions = ({
  activeVersionIndex,
  locked,
  setActiveVersionIndex,
  setVersions,
  versions,
}: {
  activeVersionIndex: number;
  locked: boolean;
  setActiveVersionIndex: (index: number) => void;
  setVersions: (versions: IVersion[]) => void;
  versions: IVersion[];
}): JSX.Element => {
  const { t } = useTranslation([translations.common, translations.offers]);
  const dispatch = useDispatch();

  const activeVersion = versions[activeVersionIndex];
  const canBeRemoved =
    !locked && versions.length > 1 && !activeVersion.isPrimary;

  const remove = () => {
    setActiveVersionIndex(
      activeVersionIndex - 1 >= 0 ? activeVersionIndex - 1 : 0
    );

    setVersions(versions.filter((version, i) => i !== activeVersionIndex));

    dispatch(
      addMessage(t(`${translations.calculationWindow}:Version removed`))
    );
  };

  const setPrimary = () =>
    setVersions(
      versions.map((version, index) => ({
        ...version,
        isPrimary: index === activeVersionIndex,
      }))
    );

  return (
    <div className="version-details-box version-actions">
      <span className="version-actions-label">
        {t(`${translations.calculationWindow}:Alternative of`, {
          count: versions.length,
          index: activeVersionIndex + 1,
        })}
        {activeVersion.isPrimary
          ? ` (${t(`${translations.calculationWindow}:primary`)})`
          : ''}
      </span>
      <span className="version-actions-buttons">
        {activeVersion.isPrimary || locked ? null : (
          <PrimaryButton className="button-one-line" onClick={setPrimary}>
            {t(`${translations.calculationWindow}:Set primary`)}
          </PrimaryButton>
        )}
        <PrimaryButton
          className="button-one-line"
          disabled={!canBeRemoved}
          onClick={remove}
        >
          {t(
            `${translations.calculationWindow}:Remove version`
          ).toLocaleUpperCase()}
        </PrimaryButton>
      </span>
    </div>
  );
};

export default VersionActions;
